import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import api from "../../api/api";
import "./CertificatePage.css";
import BackButton from "../../components/BackButton/BackButton";

function CertificatePage() {
  const { certificateId } = useParams();
  const certificateRef = useRef(null);

  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null); // 'jpg' | 'pdf' | null
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchCertificate = async () => {
      try {
        const res = await api.get(`/certificates/verify/${certificateId}`);
        setCertificate({ ...res.data, certificateId });
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchCertificate();
  }, [certificateId]);

  const captureCanvas = async () => {
    return html2canvas(certificateRef.current, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
    });
  };

  const downloadJPG = async () => {
    setDownloading("jpg");
    try {
      const canvas = await captureCanvas();
      const link = document.createElement("a");
      link.download = `learnlia-certificate-${certificateId}.jpg`;
      link.href = canvas.toDataURL("image/jpeg", 0.95);
      link.click();
    } finally {
      setDownloading(null);
    }
  };

  const downloadPDF = async () => {
    setDownloading("pdf");
    try {
      const canvas = await captureCanvas();
      const imgData = canvas.toDataURL("image/jpeg", 0.95);
      const pdf = new jsPDF("landscape", "mm", "a4");
      pdf.addImage(imgData, "JPEG", 10, 10, 277, 190);
      pdf.save(`learnlia-certificate-${certificateId}.pdf`);
    } finally {
      setDownloading(null);
    }
  };

  const shareCertificate = async () => {
    const url = window.location.href;
    const text = `I just completed the "${certificate.trackName}" learning track on Learnlia 🚀`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Learnlia Certificate", text, url });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const shareLinkedIn = () => {
    const url = encodeURIComponent(window.location.href);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, "_blank");
  };

  if (loading) {
    return (
      <div className="cert-page cert-page--loading">
        <div className="cert-spinner" />
        <p>Verifying certificate…</p>
      </div>
    );
  }

  if (!certificate?.valid) {
    return (
      <div className="cert-page cert-page--invalid">
        <div className="cert-invalid-card">
          <span className="cert-invalid-icon">✕</span>
          <h2>Invalid Certificate</h2>
          <p>This certificate ID could not be verified. It may be invalid or expired.</p>
          <a href="/" className="cert-home-btn">Return to Learnlia</a>
        </div>
      </div>
    );
  }

  const issueDate = new Date(certificate.issuedAt).toLocaleDateString("en-US", {
    year: "numeric", month: "long", day: "numeric",
  });

  return (
    <div className="cert-page">
      <div className="cert-page-inner">

        {/* Back */}
        <div className="cert-back-row">
          <BackButton to="/dashboard" label="Back to Dashboard" />
        </div>

        {/* Page header */}
        <div className="cert-page-header">
          <h1 className="cert-page-title">Certificate of Completion</h1>
          <p className="cert-page-sub">Share your achievement with the world</p>
        </div>

        {/* Action bar */}
        <div className="cert-actions">
          <button
            className="cert-action-btn cert-action-btn--outline"
            onClick={downloadJPG}
            disabled={!!downloading}
          >
            {downloading === "jpg" ? (
              <span className="cert-btn-spin" />
            ) : (
              <span className="cert-btn-icon">↓</span>
            )}
            Download JPG
          </button>

          <button
            className="cert-action-btn cert-action-btn--outline"
            onClick={downloadPDF}
            disabled={!!downloading}
          >
            {downloading === "pdf" ? (
              <span className="cert-btn-spin" />
            ) : (
              <span className="cert-btn-icon">↓</span>
            )}
            Download PDF
          </button>

          <button
            className="cert-action-btn cert-action-btn--primary"
            onClick={shareCertificate}
          >
            <span className="cert-btn-icon">⤴</span>
            {copied ? "Link Copied!" : "Share Link"}
          </button>

          <button
            className="cert-action-btn cert-action-btn--linkedin"
            onClick={shareLinkedIn}
          >
            <svg className="cert-li-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
            Add to LinkedIn
          </button>
        </div>

        {/* Certificate document */}
        <div className="cert-wrap" ref={certificateRef}>

          {/* Decorative background elements */}
          <div className="cert-bg-orb cert-bg-orb--tl" />
          <div className="cert-bg-orb cert-bg-orb--br" />
          <div className="cert-border-frame" />

          {/* Top bar */}
          <div className="cert-topbar">
            <div className="cert-brand-mark">
              <span className="cert-brand-dot" />
              Learnlia
            </div>
            <div className="cert-verified-chip">
              <span className="cert-chip-dot" />
              Verified Completion
            </div>
          </div>

          {/* Main content */}
          <div className="cert-body">

            <p className="cert-presents">presents this certificate to</p>

            <h2 className="cert-recipient">{certificate.username}</h2>

            <p className="cert-for-text">for successfully completing</p>

            <h3 className="cert-track-name">{certificate.trackName}</h3>

            {certificate.trackDescription && (
              <p className="cert-track-desc">{certificate.trackDescription}</p>
            )}

            {/* Credential info */}
            <div className="cert-meta-row">
              <div className="cert-meta-item">
                <span className="cert-meta-label">Issued On</span>
                <strong className="cert-meta-value">{issueDate}</strong>
              </div>
              <div className="cert-meta-divider" />
              <div className="cert-meta-item">
                <span className="cert-meta-label">Certificate ID</span>
                <strong className="cert-meta-value cert-id-val">{certificate.certificateId}</strong>
              </div>
              <div className="cert-meta-divider" />
              <div className="cert-meta-item">
                <span className="cert-meta-label">Issued By</span>
                <strong className="cert-meta-value">Learnlia.com</strong>
              </div>
            </div>

          </div>

          {/* Bottom bar */}
          <div className="cert-footer-bar">
            <span className="cert-footer-txt">
              Verify at learnlia.com/certificate/{certificate.certificateId}
            </span>
            {certificate.trackSlug && (
              <a
                href={`/tracks/${certificate.trackSlug}`}
                target="_blank"
                rel="noreferrer"
                className="cert-track-link"
              >
                View Track Curriculum ↗
              </a>
            )}
          </div>

        </div>

        {/* Social proof nudge */}
        <div className="cert-nudge">
          <span className="cert-nudge-icon">💡</span>
          <p>Add this to your LinkedIn profile to showcase your skills to recruiters and employers.</p>
        </div>

      </div>
    </div>
  );
}

export default CertificatePage;